import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import * as api from '@/features/backlog/api';
import { Moon, Sun } from '@/components/ui/icons';

/** The concrete theme applied to the document. */
export type Theme = 'dark' | 'light';
/** The user's CHOICE: an explicit theme, or `system` to follow the OS setting (the default). */
export type ThemePref = 'system' | Theme;
const KEY = 'spark-theme';

const isPref = (v: unknown): v is ThemePref => v === 'system' || v === 'light' || v === 'dark';

/** The OS's current colour scheme (light when unknown / no matchMedia). */
const systemTheme = (): Theme =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

/** Resolve a preference to a concrete theme (`system` → the live OS setting). */
export const resolveTheme = (p: ThemePref): Theme => (p === 'system' ? systemTheme() : p);

/** The preference persisted on THIS browser (fast-boot cache). Defaults to `system` — follow the OS. */
export const getStoredPref = (): ThemePref => {
  const s = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
  return isPref(s) ? s : 'system';
};

/** Apply a concrete theme to the document root. */
export const applyTheme = (t: Theme) => {
  document.documentElement.setAttribute('data-theme', t);
};

/** Boot helper (main.tsx): paint the stored/system theme synchronously, before React mounts. */
export const applyInitialTheme = () => applyTheme(resolveTheme(getStoredPref()));

interface ThemeCtx {
  /** The user's choice: system | light | dark. */
  pref: ThemePref;
  /** The resolved theme currently applied to the document. */
  theme: Theme;
  /** Set the preference (persisted locally AND to the user's profile, so it follows them elsewhere). */
  setPref: (p: ThemePref) => void;
  /** Flip the RESOLVED theme to the opposite explicit theme (for the simple header toggle button). */
  toggle: () => void;
}
const Ctx = createContext<ThemeCtx | null>(null);

/**
 * App-wide theme state. Precedence: the signed-in member's saved profile preference (so the choice
 * follows them across devices/logins) wins once loaded; otherwise the browser-cached preference;
 * otherwise `system` (follow the OS). `system` tracks the OS scheme live. Changing the preference
 * persists to BOTH localStorage (no-flash boot) and the profile (cross-device). We never write the
 * profile on load — an absent profile theme stays the `system` default until the user picks one.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(getStoredPref);
  const [osTheme, setOsTheme] = useState<Theme>(systemTheme);
  // Guard the profile-sync so a profile value loaded AFTER a user picks something doesn't clobber it.
  const userPicked = useRef(false);

  // Track the OS scheme so `system` follows it live.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setOsTheme(mql.matches ? 'dark' : 'light');
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const theme: Theme = pref === 'system' ? osTheme : pref;

  // Apply to the DOM + cache the preference locally whenever either changes.
  useEffect(() => { applyTheme(theme); }, [theme]);
  useEffect(() => {
    try { localStorage.setItem(KEY, pref); } catch { /* private mode — ignore */ }
  }, [pref]);

  // Cross-device sync: adopt the profile's saved theme on load (unless the user already changed it
  // this session). Absent → keep the `system` default; we don't write the profile until an explicit pick.
  useEffect(() => {
    api.getMyPreferences().then((p) => {
      if (!userPicked.current && isPref(p.theme)) setPrefState(p.theme);
    }).catch(() => undefined);
  }, []);

  const setPref = useCallback((p: ThemePref) => {
    userPicked.current = true;
    setPrefState(p);
    api.saveMyPreferences({ theme: p }).catch(() => undefined);
  }, []);

  const value = useMemo<ThemeCtx>(
    () => ({ pref, theme, setPref, toggle: () => setPref(theme === 'dark' ? 'light' : 'dark') }),
    [pref, theme, setPref],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Access the shared theme state. Requires a ThemeProvider above (mounted app-wide). */
export function useTheme(): ThemeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}

/** Header button that flips between dark and light (based on the resolved theme). */
export default function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const next: Theme = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={`Switch to ${next} theme`}
      title={`Switch to ${next} theme`}
      className="flex h-9 w-9 items-center justify-center rounded-full text-fg-muted transition-colors hover:bg-layer-strong hover:text-fg"
    >
      {theme === 'dark' ? <Sun /> : <Moon />}
    </button>
  );
}
